import { FC, RefObject, useEffect, useState } from "react";
import styled from "@emotion/styled";
import { Grow } from "./Layout/Grow";
import { Container } from "./Layout/Container";
import {
  useMotionValue,
  motion,
  animate,
  AnimatePresence,
} from "framer-motion";
import Portal from "./Layout/Portal";
import logo from "../assets/logo.webp";
import { Bars3Icon } from "@heroicons/react/24/solid";
import { useMobile } from "../MobileProvider";
import { Backdrop } from "./Layout/Backdrop";
import { logEvent } from "@firebase/analytics";
import { useApp } from "../AppProvider";

const HeaderWrapper = styled(motion.header)(() => ({
  display: "flex",
  willChange: "transform",
  flexDirection: "row",
  justifyContent: "flex-start",
  alignItems: "center",
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  zIndex: 2,
}));

const HeaderInner = styled(Container)(() => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  position: "relative",
}));

const Logo = styled(motion.div, {
  shouldForwardProp: (prop) => prop !== "img",
})<{ img: Object }>(({ img }) => ({
  backgroundImage: "url(" + img + ")",
  backgroundSize: "contain",
  backgroundRepeat: "no-repeat",
  flexShrink: 0,
  willChange: "transform",
  "@media (min-width: 768px)": {
    maxWidth: "unset",
    maxHeight: "unset",
  },
}));

const Nav = styled("nav")(() => ({
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  marginLeft: 0,
  width: "100%",
  "@media (min-width: 768px)": {
    flexDirection: "row",
    marginLeft: "1rem",
    width: "unset",
    justifyContent: "flex-start",
    gap: 0,
  },
}));

const NavLink = styled("button")(() => ({
  color: "white",
  textDecoration: "none",
  padding: "0.5rem 1rem",
  fontSize: 18,
  fontWeight: "bold",
  fontFamily: 'Oswald, "Helvetica Neue", Helvetica, Arial, sans-serif',
  textTransform: "uppercase",
  cursor: "pointer",
  backgroundColor: "transparent",
  border: "none",
  whiteSpace: "nowrap",
}));

const MenuButton = styled(motion.button)(() => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  padding: "2rem",
  fontSize: 18,
  fontWeight: "bold",
  color: "#fff",
  backgroundColor: "transparent",
  border: "none",
  cursor: "pointer",
  "@media (min-width: 900px)": {
    display: "none",
  },
  "& svg": {
    width: 24,
    height: 24,
    fill: "#fff",
  },
}));

const SidebarWrapper = styled(motion.div)(() => ({
  position: "fixed",
  top: 0,
  left: "25%",
  bottom: 0,
  right: 0,
  zIndex: 3,
  padding: "2rem 0",
  display: "flex",
  flexDirection: "column",
  backgroundColor: "#000",
  color: "#fff",
}));

let lastScrollTop = 0;

type HeaderProps = {
  studioRef: RefObject<HTMLDivElement | null>;
  projekteRef: RefObject<HTMLDivElement | null>;
  aboutRef: RefObject<HTMLDivElement | null>;
  jobsRef: RefObject<HTMLDivElement | null>;
  teamRef: RefObject<HTMLDivElement | null>;
  kontaktRef: RefObject<HTMLDivElement | null>;
};

export const Header: FC<HeaderProps> = (props) => {
  const mobile = useMobile();
  const { analytics } = useApp();
  const height = useMotionValue(200);
  const [backgroundOpacity, setBackgroundOpacity] = useState(0);
  const y = useMotionValue("0%");
  const [sidebar, setSidebar] = useState<boolean>(false);
  const [scrolled, setScrolled] = useState<boolean>(false);
  const [showLogo, setShowLogo] = useState<boolean>(false);

  useEffect(() => {
    if (sidebar) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [sidebar]);

  useEffect(() => {
    if (!mobile) {
      setSidebar(false);
    }
  }, [mobile]);

  useEffect(() => {
    const didScroll = () => {
      if (Math.abs(lastScrollTop - window.scrollY) <= 5) {
        return;
      }
      if (window.scrollY > lastScrollTop && window.scrollY > 500) {
        animate(y, "-100%", {
          stiffness: 120,
          damping: 20,
          duration: 0.1,
          type: "spring",
        });
      } else {
        animate(y, "0%", {
          stiffness: 120,
          damping: 20,
          duration: 0.1,
          type: "spring",
        });
      }

      lastScrollTop = window.scrollY;
    };

    const interval = setInterval(() => {
      if (scrolled) {
        setScrolled(false);
        didScroll();
      }
    }, 250);

    return () => {
      clearInterval(interval);
    };
  }, [scrolled, y]);

  useEffect(() => {
    // check if window scroll is greated than 50
    const handleScroll = async () => {
      setScrolled(true);
      if (window.scrollY < 100) {
        animate(height, 200);
        setBackgroundOpacity(0);
        setShowLogo(false);
      } else if (window.scrollY >= 100 && window.scrollY < 250) {
        animate(height, 100);
        setBackgroundOpacity(0.5);
        setShowLogo(true);
      } else {
        animate(height, 100);
        setBackgroundOpacity(1);
        setShowLogo(true);
      }
    };

    // add event listener
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("load", handleScroll, true);

    // remove event listener on cleanup
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("load", handleScroll);
    };
  }, [height, y]);

  const handleNavigation = (ref: RefObject<HTMLDivElement | null>) => {
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth" });
      if (analytics) {
        logEvent(analytics, "navigation_click", {
          page_path: ref.current?.id,
        });
      }
    }
    if (sidebar) {
      setSidebar(false);
    }
  };

  const Navigation: FC = () => (
    <Nav>
      <NavLink
        type={"button"}
        onClick={() => {
          window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
          if (sidebar) {
            setSidebar(false);
          }
          if (analytics) {
            logEvent(analytics, "navigation_click", { page_path: "home" });
          }
        }}
      >
        Home
      </NavLink>
      <NavLink
        type={"button"}
        onClick={() => handleNavigation(props.projekteRef)}
      >
        Projekte
      </NavLink>
      <NavLink
        type={"button"}
        onClick={() => handleNavigation(props.studioRef)}
      >
        Studio
      </NavLink>
      <NavLink type={"button"} onClick={() => handleNavigation(props.aboutRef)}>
        About us
      </NavLink>
      <NavLink type={"button"} onClick={() => handleNavigation(props.jobsRef)}>
        Jobs
      </NavLink>
      <NavLink type={"button"} onClick={() => handleNavigation(props.teamRef)}>
        Team
      </NavLink>
      <NavLink
        type={"button"}
        onClick={() => handleNavigation(props.kontaktRef)}
      >
        Kontakt
      </NavLink>
    </Nav>
  );

  return (
    <Portal wrapperId={"header"}>
      <HeaderWrapper
        style={{ height, y }}
        animate={{ background: "rgba(0,7,31," + backgroundOpacity + ")" }}
      >
        <HeaderInner>
          {showLogo && (
            <Logo
              initial={{ width: 100, height: 100 }}
              animate={{
                width: backgroundOpacity > 0 ? 50 : mobile ? 80 : 100,
                height: backgroundOpacity > 0 ? 50 : mobile ? 80 : 100,
              }}
              img={logo}
            />
          )}
          <Grow />
          {!mobile && <Navigation />}
          <MenuButton type={"button"} onClick={() => setSidebar(true)}>
            <Bars3Icon />
          </MenuButton>
        </HeaderInner>
      </HeaderWrapper>
      <AnimatePresence>
        {sidebar && (
          <Backdrop
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setSidebar(false)}
            style={{ zIndex: 10 }}
          >
            <SidebarWrapper
              onClick={(e) => e.stopPropagation()}
              initial={{ x: "100%" }}
              animate={{ x: 0, transition: { duration: 0.3 } }}
              exit={{ x: "100%" }}
            >
              <Navigation />
              {mobile && (
                <div
                  style={{
                    marginTop: "auto",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <NavLink type={"button"}>Impressum</NavLink>
                  <NavLink type={"button"}>Datenschutz</NavLink>
                </div>
              )}
            </SidebarWrapper>
          </Backdrop>
        )}
      </AnimatePresence>
    </Portal>
  );
};
